// biome-ignore lint/style/useNodejsImportProtocol: its ok to use relative path here
import { CN_HOME_BUNDLE_SECTION_DISPLAY } from '@/const/cookies';
import { usePartnerId } from '@/store/usePartnerId';
import { Button } from '@lemonade-hq/nuis';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import { useRouter } from 'next/router';
import qs from 'querystring';
import type React from 'react';
import { type HTMLAttributes, forwardRef } from 'react';
import styled from 'styled-components';

export const ONBOARDING_START_PATH = '/onboarding/start';
export const PRODUCT_QUERY_PARAM = 'p';
export const BASE_EXTERNAL_EXCLUDED_PARAMS = [
  'f',
  'hide_banner',
  'locale',
  'region',
  'lang',
  'quotePath',
  'hide_banner',
  'userFirstName',
  'userLastName',
  'settingsPath',
  'myAccountPath',
  'logOutPath',
  'isAdaEnabled',
  CN_HOME_BUNDLE_SECTION_DISPLAY,
]; // `f` is always added by default anyway, added to prevent duplication

const CTAButton = styled(Button)`
  pointer-events: auto;
  &:hover {
    text-decoration: none;
  }
`;

type CTAButtonProps = React.ComponentProps<typeof Button>;

// this should extend CTAButtonProps, but Button props are not properly exported and no easy way to infer it because of styled-components
interface Props extends HTMLAttributes<HTMLAnchorElement> {
  product?: string;
  children: React.ReactNode;
  excludedParams?: string[];
  analyticsParams?: Record<string, string>;
  customUrl?: string;
  url?: string;
  minWidth?: number;
  size?: string;
  flavor?: string;
  id?: string;
  glowing?: boolean;
  disabled?: boolean;
  absoluteUrl?: string;
}

function isHTMLAnchorElement(value: unknown): value is HTMLAnchorElement {
  return value instanceof HTMLAnchorElement;
}

const JoinCTA = forwardRef((props: Props, ref) => {
  const router = useRouter();

  const {
    product,
    children,
    excludedParams = [],
    analyticsParams,
    customUrl,
    onClick,
    absoluteUrl,
    ...restButtonProps
  } = props;
  const { trackEvent } = useAnalyticsContext();
  const partnerId = usePartnerId(state => state.partnerId);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (onClick != null) {
      onClick(e);
      return;
    }
    if (isHTMLAnchorElement(e.target)) {
      const source = e.target.getAttribute('data-source');

      if (source !== null && source !== '') {
        trackEvent(`get_${source}_insurance_clicked`, analyticsParams ?? {});
      }
    }
  };

  const urlParams = router.query;
  const isFrenchPage = router.pathname.startsWith('/fr');
  const queryParams: Record<string, string | string[] | undefined> =
    product && !isFrenchPage ? { [PRODUCT_QUERY_PARAM]: product } : {};

  const allExcludedParams = BASE_EXTERNAL_EXCLUDED_PARAMS.concat(excludedParams);
  Object.keys(urlParams).forEach(key => {
    if (!allExcludedParams.includes(key)) {
      queryParams[key] = urlParams[key];
    }
  });
  if (partnerId != null) {
    queryParams.partner_id = String(partnerId);
  }

  const queryString = queryParams ? qs.stringify(queryParams) : '';
  const formatCustomUrl = (): string | undefined => {
    if (customUrl == null) {
      return undefined;
    }

    const sanitizedUrl = customUrl.endsWith('?') ? customUrl.slice(0, -1) : customUrl;
    if (!queryString) {
      return sanitizedUrl;
    }

    const [baseUrl, customUrlQueryParams] = sanitizedUrl.split('?');
    if (!customUrlQueryParams) {
      return `${baseUrl}?${queryString}`;
    }
    const combinedQueryParams = `${new URLSearchParams(customUrlQueryParams).toString()}&${queryString}`;

    return `${baseUrl}?${combinedQueryParams}`;
  };
  const url = formatCustomUrl() ?? `${ONBOARDING_START_PATH}?${queryString}`;

  return (
    <CTAButton
      ref={ref}
      as="a"
      href={absoluteUrl ?? url}
      {...(restButtonProps as CTAButtonProps)}
      onClick={handleClick}
    >
      {children}
    </CTAButton>
  );
});

JoinCTA.displayName = 'JoinCTA';

export default JoinCTA;
